import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { getProtocol } from "@/lib/protocols/definitions";
import { startProtocol, completeProtocolStep, getMyProtocols, undoLastProtocolStep, extendProtocolSession } from "@/lib/protocols.functions";
import { useLocale, useT } from "@/i18n/locale-context";
import { useJurisdiction } from "@/hooks/useJurisdiction";
import { getDisclaimerPack } from "@/lib/jurisdiction";
import { supabase } from "@/integrations/supabase/client";
import { ReferralBanner } from "@/components/ReferralBanner";
import { PhotoGuide } from "@/components/protocols/PhotoGuide";
import { getPhaseGoal } from "@/lib/protocols/phase-goal";
import { usePremium } from "@/hooks/usePremium";
import { isProtocolUnlocked, isProBillingConfigured } from "@/lib/feature-flags";

export const Route = createFileRoute("/protocols/$goal")({
  head: () => ({ meta: [{ title: "Protocole — Glow Up Coach" }] }),
  component: ProtocolPage,
});

function ProtocolPage() {
  const { goal } = Route.useParams();
  const def = getProtocol(goal);
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { country, jurisdiction } = useJurisdiction();
  const pack = getDisclaimerPack(jurisdiction, locale);
  const { isPremium, isPro } = usePremium();

  const start = useServerFn(startProtocol);
  const complete = useServerFn(completeProtocolStep);
  const fetchMine = useServerFn(getMyProtocols);
  const undoLast = useServerFn(undoLastProtocolStep);
  const extend = useServerFn(extendProtocolSession);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
    });
  }, [nav]);

  const { data } = useQuery({
    queryKey: ["my-protocols"],
    queryFn: () => fetchMine(),
  });

  if (!def) {
    return (
      <div className="min-h-screen bg-background text-foreground p-12 text-center">
        <p>{t("protocols.not_found")}</p>
        <Link to="/protocols" className="underline mt-4 inline-block">{t("protocols.back")}</Link>
      </div>
    );
  }

  const unlocked = isProtocolUnlocked(def, { isPremium, isPro });
  const isProLocked = def.tier === "pro" && !unlocked;

  const session = data?.sessions.find((s) => s.goal === goal && s.status === "active");
  const events = data?.events.filter((e) => session && e.session_id === session.id) ?? [];
  const doneKeys = new Set(events.map((e) => e.step_key));
  const isExtended = Boolean((session as any)?.extended);
  // Steps actually displayed: always core; append extension once unlocked.
  const visibleSteps = [
    ...def.steps,
    ...(isExtended && def.extension ? def.extension.steps : []),
  ];
  // First not-yet-done step that expects a selfie — the "current phase" the
  // user should photograph right now.
  const currentStep = visibleSteps.find((s) => s.selfieExpected && !doneKeys.has(s.key)) ?? null;
  // Core phase is finished → propose the extension opt-in.
  const corePhaseDone = def.steps.every((s) => doneKeys.has(s.key));
  const canOfferExtension = Boolean(session && def.extension && !isExtended && corePhaseDone);
  // Most recent done step (by done_at) — the only one undoable
  const lastDoneKey = [...events]
    .sort((a, b) => new Date(b.done_at).getTime() - new Date(a.done_at).getTime())[0]?.step_key;

  async function onStart() {
    try {
      await start({ data: { goal, country: country ?? undefined, jurisdiction } });
      qc.invalidateQueries({ queryKey: ["my-protocols"] });
      toast.success(t("protocols.toast.started"));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  }

  async function onCompleteStep(stepKey: string) {
    if (!session) return;
    try {
      await complete({ data: { sessionId: session.id, stepKey } });
      qc.invalidateQueries({ queryKey: ["my-protocols"] });
      toast.success(t("protocols.toast.step_done"));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  }

  async function onUndoLast() {
    if (!session) return;
    try {
      const res = await undoLast({ data: { sessionId: session.id } });
      qc.invalidateQueries({ queryKey: ["my-protocols"] });
      if (res.undone) toast.success(t("protocols.toast.undone"));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  }

  async function onExtend() {
    if (!session) return;
    try {
      await extend({ data: { sessionId: session.id } });
      qc.invalidateQueries({ queryKey: ["my-protocols"] });
      toast.success(t("protocols.toast.extended"));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/protocols" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
          ← {t("protocols.back")}
        </Link>
        <h1 className="text-3xl font-bold mt-3">{def.title[locale]}</h1>
        <p className="mt-2 text-muted-foreground">{def.subtitle[locale]}</p>

        <div className="mt-5 rounded-lg border border-foreground/10 bg-foreground/[0.02] p-4 text-sm">
          <strong className="block mb-1">{t("disclaimer.banner.label")}</strong>
          {pack.banner} <span className="text-muted-foreground">· {pack.emergency}</span>
        </div>

        {def.referralRequired && (
          <ReferralBanner
            variant={def.goal === "pregnancy_postpartum" ? "midwife" : "dermato"}
          />
        )}

        <PhotoGuide defaultOpen={!session} goal={def.goal} />

        {!session && !isProLocked && (
          <button
            onClick={onStart}
            className="mt-6 bg-foreground text-background px-5 py-2.5 rounded-full text-sm font-semibold"
          >
            {t("protocols.cta.start")}
          </button>
        )}

        {isProLocked && (
          <aside className="mt-6 rounded-2xl border border-primary/30 bg-primary/[0.04] p-5">
            <p className="text-[11px] uppercase tracking-widest text-primary">{t("protocols.badge.pro")}</p>
            <h2 className="font-semibold text-lg mt-1">{t("protocols.locked.detail.title")}</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              {t("protocols.locked.detail.desc")}
            </p>
            <p className="text-sm font-medium text-primary mt-2">{t("protocols.locked.trial")}</p>
            {isProBillingConfigured() ? (
              <Link
                to="/checkout"
                search={{ plan: "protocols_pro_monthly" as const }}
                className="mt-4 inline-block bg-primary text-primary-foreground rounded-full px-5 py-2.5 text-sm font-semibold"
              >
                {t("protocols.locked.cta")}
              </Link>
            ) : (
              <div className="mt-4 rounded-md bg-foreground/[0.04] px-4 py-3 text-sm">
                {t("protocols.locked.coming_soon")}
              </div>
            )}
            <p className="mt-3 text-[11px] text-muted-foreground italic">
              {t("protocols.locked.detail.preview")}
            </p>
          </aside>
        )}

        {session && currentStep && (
          <div className="mt-6 rounded-2xl border border-accent/40 bg-accent/5 p-4">
            <p className="text-[11px] uppercase tracking-widest text-accent-foreground/80">
              {t("protocols.current_step.kicker")}
            </p>
            <p className="font-semibold mt-1">{currentStep.title[locale]}</p>
            <Link
              to="/protocols/$goal/capture/$stepKey"
              params={{ goal, stepKey: currentStep.key }}
              className="mt-3 inline-block bg-foreground text-background rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              {t("protocols.current_step.cta")}
            </Link>
          </div>
        )}

        <ol className="mt-8 space-y-4">
          {visibleSteps.map((s, i) => {
            const done = doneKeys.has(s.key);
            const isExtensionStep = i >= def.steps.length;
            return (
              <li key={s.key} className={`rounded-xl border p-4 ${done ? "border-accent/40 bg-accent/5" : "border-foreground/10"} ${isExtensionStep ? "border-dashed" : ""}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {String(i + 1).padStart(2, "0")} · {s.dayOffset >= 0 ? `D+${s.dayOffset}` : `D${s.dayOffset}`}
                      {isExtensionStep && <span className="ml-2 uppercase tracking-widest text-[10px] text-accent-foreground/80">· {t("protocols.extension.badge")}</span>}
                    </p>
                    <h3 className="font-semibold mt-0.5">{s.title[locale]}</h3>
                    <p className="text-xs text-muted-foreground italic mt-1 leading-snug">
                      → {getPhaseGoal(s.dayOffset, locale, s.phaseGoal)}
                    </p>
                  </div>
                  {session && !done && (
                    <button
                      onClick={() => onCompleteStep(s.key)}
                      className="text-xs bg-foreground text-background rounded-full px-3 py-1.5"
                    >
                      {t("protocols.cta.mark_done")}
                    </button>
                  )}
                  {done && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-accent-foreground bg-accent/20 rounded-full px-2 py-1">
                        {t("protocols.badge.done")}
                      </span>
                      {s.key === lastDoneKey && (
                        <button
                          onClick={onUndoLast}
                          className="text-xs rounded-full px-3 py-1.5 border border-foreground/20 hover:bg-foreground/5"
                          title={t("protocols.cta.undo_hint")}
                        >
                          {t("protocols.cta.undo")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <ul className="mt-3 text-sm text-muted-foreground list-disc list-inside space-y-1">
                  {s.checklist[locale].map((c, k) => <li key={k}>{c}</li>)}
                </ul>
                {s.selfieExpected && (
                  <Link
                    to="/protocols/$goal/capture/$stepKey"
                    params={{ goal, stepKey: s.key }}
                    className="mt-3 inline-block text-xs underline text-foreground/80"
                  >
                    {t("protocols.cta.add_selfie")}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>

        {canOfferExtension && def.extension && (
          <div className="mt-8 rounded-2xl border border-accent/40 bg-accent/5 p-5">
            <p className="text-[11px] uppercase tracking-widest text-accent-foreground/80">
              {t("protocols.extension.kicker")}
            </p>
            <h3 className="font-semibold mt-1 text-lg">{def.extension.label[locale]}</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              {def.extension.rationale[locale]}
            </p>
            <ul className="mt-3 text-xs text-muted-foreground list-disc list-inside space-y-0.5">
              {def.extension.steps.map((s) => (
                <li key={s.key}>{s.title[locale]}</li>
              ))}
            </ul>
            <button
              onClick={onExtend}
              className="mt-4 bg-foreground text-background rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              {t("protocols.extension.cta")}
            </button>
          </div>
        )}

        {session && def.extension && !canOfferExtension && !isExtended && (
          <p className="mt-6 text-xs text-muted-foreground italic">
            {t("protocols.extension.preview")} <span className="font-semibold">{def.extension.label[locale]}</span>
          </p>
        )}
      </main>
    </div>
  );
}
